import React, { Component, PropTypes } from 'react';
import RadialSVGProgressBar from 'components/Widgets/RadialSVGProgressBar';

import { DragSource, DropTarget } from 'react-dnd';
import { IconButton, Paper, Styles } from 'material-ui';
import { ItemTypes } from 'Constants';
import * as Icons from 'components/Material/SvgIcons';
import { CourseActions } from 'actions';
import { ThemeStore } from 'stores';
import { Colors, Text } from 'components/Material/Styles/Colors';
import CSSTransitionGroup from 'react-addons-css-transition-group';
import { findDOMNode } from 'react-dom';
import TextField from './TextField';
import styles from './styles';
import { ClipboardActions } from 'actions';
import Message from 'services/Message';

const { Spacing } = Styles;

const pageSource = {
  beginDrag(props) {
    return {
      index: props.index,
      sectionIndex: props.sectionIndex
    };
  }
};

const pageTarget = {
  hover(props, monitor, component) {
    const item = monitor.getItem();
    const dragIndex = item.index;
    const sectionDragIndex = item.sectionIndex;
    const hoverIndex = props.index;
    const sectionHoverIndex = props.sectionIndex;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex && sectionDragIndex === sectionHoverIndex) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (
      dragIndex < hoverIndex &&
      sectionDragIndex === sectionHoverIndex &&
      hoverClientY < hoverMiddleY
    ) {
      return;
    }

    // Dragging upwards
    if (
      dragIndex > hoverIndex &&
      sectionDragIndex === sectionHoverIndex &&
      hoverClientY > hoverMiddleY
    ) {
      return;
    }

    props.onMovePage(
      sectionDragIndex,
      dragIndex,
      sectionHoverIndex,
      hoverIndex
    );

    monitor.getItem().index = hoverIndex;
    monitor.getItem().sectionIndex = sectionHoverIndex;
  }
};

class Page extends Component {
  static displayName = 'Page';

  static propTypes = {
    onTouchTap: PropTypes.func.isRequired,
    onMovePage: PropTypes.func,
    onDelete: PropTypes.func,
    name: PropTypes.string.isRequired,
    progress: PropTypes.number,
    id: PropTypes.number,
    sectionId: PropTypes.number,
    reader: PropTypes.bool,
    editor: PropTypes.bool
  };

  static contextTypes = {
    muiTheme: PropTypes.object,
    getIntlMessage: PropTypes.func
  };

  static defaultProps = {
    progress: 0
  };

  constructor() {
    super(...arguments);
    this.state = {
      name: null,
      mode: this.props.mode || 'detail',
      mouseOver: false,
      controlsExpanded: false
    };
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.currentPage && nextProps.currentPage) {
      this.props.scrollIntoView(findDOMNode(this));
    }
  }

  movePage(source, target, sourceSection, targetSection) {
    if (this.props.onMovePage) {
      this.props.onMovePage(source, target, sourceSection, targetSection);
    }
  }

  deletePage(pageId, e) {
    if (this.props.onDelete) {
      this.props.onDelete(pageId);
    }
    e.preventDefault();
  }

  edit() {
    this.setState({ mode: 'edit' });
  }

  trySave() {
    const name = this.refs.textfield.getValue();
    if (name.trim().length != 0) {
      CourseActions.updatePage({ id: this.props.id, name });
      this.setState({ name, mode: 'detail' });
    } else {
      Message.info('Page name cannot be a space!!');
    }
  }

  expandControls() {
    this.setState({ controlsExpanded: true });
  }

  collapseControls() {
    this.setState({ controlsExpanded: false });
  }

  mouseOver(mouseOver = false) {
    const newState = {
      mouseOver
    };
    if (!mouseOver) {
      newState.controlsExpanded = false;
    }
    this.setState(newState);
  }

  save() {
    this.setState({ mode: 'detail' });
  }

  getStyle(overrides, hoverOverride) {
    const { progress, isDragging, theme } = this.props;

    const style = {
      opacity: isDragging ? 0 : 1
    };

    const hoverStyle = {
      backgroundColor: 'rgba(200, 200, 200, 0.1)',
      borderColor: ThemeStore.getColor(theme, 0.5)
    };
    return Object.assign(
      {},
      styles.page,
      progress === 1 ? styles.inactivePageHeader : styles.activePageHeader,
      this.props.style,
      this.props.currentPage
        ? hoverOverride ? hoverOverride :
        {
            borderColor: ThemeStore.getColor(theme),
            color: ThemeStore.getColor(theme)
          }
        : {},
      style,
      this.state.mouseOver ? hoverOverride ? hoverOverride : hoverStyle : {},
      overrides
    );
  }

  getTaskStyle(style) {
    return Object.assign(
      {},
      {
        height: 2,
        lineHeight: '8px',
        marginLeft: 26,
        marginRight: 18,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'rgba(255, 255, 255, 0.25)'
      },
      style
    );
  }

  duplicate() {
    CourseActions.duplicatePage(this.props.courseId, this.props.id);
  }

  copy() {
    ClipboardActions.copy({
      type: 'page',
      id: this.props.id,
      data: this.props.data
    });
  }

  addPage() {
    CourseActions.addPage(
      this.props.sectionId,
      this.props.id,
      this.context.getIntlMessage('page.new')
    );
  }

  render() {
    let name, controls, progress;
    const { tasks, connectDragSource, connectDropTarget, isQuiz } = this.props;
    let answered;

    const iconButtonStyle = {
      backgroundColor: Colors.fullWhite,
      height: 38,
      width: 38,
      padding: (38 - 24) / 2
    };
    progress = (
      <RadialSVGProgressBar
        style={{ float: 'right' }}
        size={48}
        amount={this.props.progress * 100}
      />
    );
    if (this.state.mode === 'edit') {
      const extraStyles = isQuiz && { color: 'white'};
      const extraUnderlineStyls = isQuiz && { borderColor: 'white' }
      name = (
        <TextField
          inputStyle={{...styles.textField, ...extraStyles }}
          underlineFocusStyle={extraUnderlineStyls}
          onEnterKeyDown={this.trySave.bind(this)}
          fullWidth
          ref="textfield"
          defaultValue={this.state.name || this.props.name}
        />
      );
      controls = (
        <div
          className="controls"
          style={{
            position: 'absolute',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap'
          }}
          key="controls-done"
        >
          <IconButton
            touch
            tooltip={this.context.getIntlMessage('Save')}
            tooltipPosition="bottom-left"
            style={iconButtonStyle}
            onTouchTap={this.trySave.bind(this)}
          >
            <Icons.ActionVisibility />
          </IconButton>
        </div>
      );
    } else {
      let moreControls;
      if (tasks && tasks.count > 0) {
        const answeredTasks = [];
        for (let i = 0; i < tasks.count; i++) {
          if (i < tasks.answered) {
            answeredTasks.push(<div style={styles.answeredTask} key={i} />);
          } else {
            answeredTasks.push(<div style={styles.unansweredTask} key={i} />);
          }
        }
        answered = (
          <div style={this.getTaskStyle(isQuiz ? { backgroundColor: 'transparent' } : null)}>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                height: 2,
                width: `${tasks.answered * 100 / tasks.count}%`
              }}
            />
          </div>
        );
      }
      name = (
        <div onTouchTap={this.props.onTouchTap}>
          {this.state.name || this.props.name}
        </div>
      );
      if (this.state.controlsExpanded) {
        moreControls = (
          <div>
            {/* <IconButton touch tooltip={this.context.getIntlMessage('Copy')} style={iconButtonStyle} onTouchTap={this.copy.bind(this)}><Icons.ContentContentCopy hoverColor={'#777'} /></IconButton> */}
            <IconButton
              touch
              tooltip={this.context.getIntlMessage('Duplicate')}
              tooltipPosition="bottom-left"
              style={iconButtonStyle}
              onTouchTap={this.duplicate.bind(this)}
            >
              <Icons.ImageFilterNone hoverColor={'#777'} />
            </IconButton>
            <IconButton
              touch
              tooltip={this.context.getIntlMessage('Edit')}
              tooltipPosition="bottom-left"
              style={iconButtonStyle}
              onTouchTap={this.edit.bind(this)}
            >
              <Icons.ContentCreate hoverColor={'#777'} />
            </IconButton>
            {this.props.pages.length > 1 && <IconButton
              touch
              tooltip={this.context.getIntlMessage('Remove')}
              tooltipPosition="bottom-left"
              style={iconButtonStyle}
              onTouchTap={this.deletePage.bind(this, this.props.id)}
            >
              <Icons.ActionDelete hoverColor={'#777'} />
            </IconButton>}
          </div>
        );
      } else {
        moreControls = (
          <div>
            <IconButton
              touch
              tooltip={this.context.getIntlMessage('More')}
              tooltipPosition="bottom-left"
              style={iconButtonStyle}
              onTouchTap={this.expandControls.bind(this)}
            >
              <Icons.NavigationMoreHoriz hoverColor={'#777'} />
            </IconButton>
            <IconButton
              touch
              tooltip={this.context.getIntlMessage('page.new')}
              tooltipPosition="bottom-left"
              style={iconButtonStyle}
              onTouchTap={this.addPage.bind(this)}
            >
              <Icons.ContentAdd hoverColor={'#777'} />
            </IconButton>
          </div>
        );
      }
      if (this.state.mouseOver && this.props.editor) {
        controls = (
          <Paper className="controls" style={styles.controls} key="controls">
            {moreControls}
          </Paper>
        );
      }
    }
    return connectDragSource(
      connectDropTarget(
        <div>
          <div
            style={this.getStyle(isQuiz && { color: 'white'}, isQuiz && { borderColor: 'rgba(255, 255, 255, 0.5)' })}
            onMouseEnter={this.mouseOver.bind(this, true)}
            onMouseLeave={this.mouseOver.bind(this, false)}
          >
            {name}
            <CSSTransitionGroup
              transitionName="controls"
              component="div"
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0 }}
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {controls}
            </CSSTransitionGroup>
          </div>
          {answered}
        </div>
      )
    );
  }
}

export default DropTarget(ItemTypes.TOC_PAGE, pageTarget, connect => ({
  connectDropTarget: connect.dropTarget()
}))(
  DragSource(ItemTypes.TOC_PAGE, pageSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
  }))(Page)
);



// WEBPACK FOOTER //
// ./src/js/components/Course/TableOfContents/Page.js